import { insertAndSyncOrderEntryItems } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/collections/insert-and-sync-order-entry-items';
import { OrderEntryPriceEditDisplayHeader } from '@/types';
import { orderGuideItemCollection } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/collections/order-entry-collection';
import { useCallback, useRef } from 'react';
import { useGeneratedGetOrderEntryItemsFromVariantPackIdsLazyQuery } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/documents/get-order-entry-items-from-variant-pack-ids.generated';
import { useSalesRepConfig } from '@/experiences/sales-portal/blocs/hooks/use-sales-rep-config';

interface UseVariantPackPrefetchLoaderParams {
  chatUUID: string | null;
  deliveryDate: string | null;
  priceEditDisplayHeaders: OrderEntryPriceEditDisplayHeader[];
  restaurantId: string;
  supplierId: string;
}

export interface UseVariantPackPrefetchLoaderResult {
  prefetchVariantPacks: (variantPackIds: readonly string[]) => Promise<void>;
}

/**
 * Fetches variant pack rows in the background when item details load, so they
 * can be added to the review tab immediately.
 *
 * @param params - {@link UseVariantPackPrefetchLoaderParams}.
 * @returns `prefetchVariantPacks(variantPackIds)` — fire-and-forget; awaitable
 *   in tests.
 */
export const useVariantPackPrefetchLoader = ({
  chatUUID,
  deliveryDate,
  priceEditDisplayHeaders,
  restaurantId,
  supplierId,
}: UseVariantPackPrefetchLoaderParams): UseVariantPackPrefetchLoaderResult => {
  const { salesRepConfig, loading: salesRepConfigLoading } = useSalesRepConfig(
    supplierId,
    chatUUID,
  );
  const fetchVariantPackItems =
    useGeneratedGetOrderEntryItemsFromVariantPackIdsLazyQuery({
      fetchPolicy: 'no-cache',
    });
  const requestedRef = useRef<Set<string>>(new Set());

  const prefetchVariantPacks = useCallback(
    async (variantPackIds: readonly string[]): Promise<void> => {
      // Mirror the detail loader's skip-when-no-fulfillment: without a delivery
      // date the panel can't open against these rows anyway.
      if (!deliveryDate) {
        return;
      }

      // Defer while sales rep config is still loading so prefetched rows aren't
      // built with a not-yet-loaded config (which would give wrong price-edit
      // display headers). Rows fall back to on-demand loading when drilled into.
      if (salesRepConfigLoading) {
        return;
      }

      // Dedupe: drop falsy ids, in-call duplicates, ids already in the
      // collection, and ids already requested this mount. Mark in-flight
      // before awaiting so a re-render firing concurrently doesn't double-fetch.
      const idsToFetch = Array.from(
        new Set(variantPackIds.filter(Boolean)),
      ).filter(
        (id) =>
          !orderGuideItemCollection.get(id) && !requestedRef.current.has(id),
      );
      if (idsToFetch.length === 0) {
        return;
      }
      for (const id of idsToFetch) {
        requestedRef.current.add(id);
      }

      try {
        const result = await fetchVariantPackItems({
          variables: {
            deliveryDate,
            restaurantId,
            supplierId,
            variantPackIds: idsToFetch,
          },
        });
        const rawItems =
          result.data?.getOrderEntryItemsFromVariantPackIds
            ?.order_entry_items ?? [];

        // Insert + sync via the shared helper, which skips ids a concurrent
        // path (cart / order guide / catalog search) may have inserted while
        // this request was in flight.
        insertAndSyncOrderEntryItems(
          rawItems,
          priceEditDisplayHeaders,
          salesRepConfig,
        );
      } catch {
        // Background fetch — must not break the panel. Un-mark so a later
        // drill-in (which fires its own detail query + prefetch) can retry.
        for (const id of idsToFetch) {
          requestedRef.current.delete(id);
        }
      }
    },
    [
      deliveryDate,
      fetchVariantPackItems,
      priceEditDisplayHeaders,
      restaurantId,
      salesRepConfig,
      salesRepConfigLoading,
      supplierId,
    ],
  );

  return { prefetchVariantPacks };
};
